import React, { useEffect } from 'react'
import { useStore } from "../../../stores/store";
import { useTranslation } from "react-i18next";
import './Dashboard.css';

function Dashboard() {
    const { mainStore } = useStore();
    const { t } = useTranslation();

    useEffect(() => {
        mainStore.setMenuType(mainStore.DashboardMenuType);
        mainStore.setMenuNo(mainStore.DashboardMenuNo);
    }, []);

    const startDate = new Date('2021-10-01');
    const endDate   = new Date('2025-10-01');
    const now       = new Date();
    const pct       = Math.min(Math.round(((now - startDate) / (endDate - startDate)) * 100), 100);
    const msLeft    = endDate - now;
    const daysLeft  = msLeft > 0 ? Math.ceil(msLeft / 86400000) : 0;
    const moLeft    = msLeft > 0 ? Math.ceil(daysLeft / 30) : 0;

    const r  = 52;
    const cx = 65;
    const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;

    const pubs = [
        {
            title: "Design of an Online Platform EDA-DYVI to study the Dynamics and Forecast the Viability of EDA Companies",
            authors: "B. Zylfiu, G. Marinova, E. Hajrizi",
            venue: "RIM 2023, University of Bihac, Sep. 2023",
            status: "published",
            detail: "Conference · ISSN 2566-3283"
        },
        {
            title: "Review of Artificial Intelligence Implementation in Electronic Design Automation Methods and Tools",
            authors: "K. Kostova, L. Menxhiqi, B. Zylfiu, G. Marinova",
            venue: "UBT IC-CS, Kosovo, Oct. 2023",
            status: "published",
            detail: "Conference · Cited by 1 · DOI: 10.33107/ubt-ic.2023.284"
        },
        {
            title: "A Web-Based Application in EDA-DYV Platform for Analyzing Mergers and Acquisitions in the EDA",
            authors: "B. Zylfiu, G. Marinova",
            venue: "IFAC-PapersOnLine, vol. 58, no. 3, pp. 193–197 · TECIS 2024, Waterford",
            status: "published",
            detail: "Scopus · Web of Science · Cited by 1 · DOI: 10.1016/j.ifacol.2024.07.149"
        },
        {
            title: "AI-Driven M&A Forecasting: Singular Spectrum Analysis Model in the EDA Industry",
            authors: "B. Zylfiu, G. Marinova",
            venue: "ICEST 2025, Ohrid, North Macedonia, pp. 1–4",
            status: "published",
            detail: "IEEE Xplore · Scopus · Web of Science · DOI: 10.1109/ICEST66328.2025.11098239"
        },
        {
            title: "Cluster Analysis of Merger and Acquisition Patterns in the Electronic Design Automation Industry Using Machine Learning Techniques",
            authors: "B. Zylfiu, G. Marinova, E. Hajrizi, B. Qehaja",
            venue: "IJITIS, vol. 8, no. 3, pp. 784–817, 2025",
            status: "published",
            detail: "Journal · Scopus CiteScore 2.52 · Cited by 1 · DOI: 10.15157/IJITIS.2025.8.3.784-817"
        },
        {
            title: "An Intelligent Multi-Algorithm Integration Framework for Automated M&A Decision Support in Technology-Intensive Industries",
            authors: "B. Zylfiu",
            venue: "ICISNA'25, Vol. 3, Antalya, Turkey, Dec. 2025, pp. 1–7",
            status: "published",
            detail: "Conference · DOI: 10.58190/icisna.2025.133"
        },
        {
            title: "Implementing a Semi-Automated M&A Discovery Module for the EDA-DYV Research Platform",
            authors: "B. Zylfiu, G. Marinova",
            venue: "",
            status: "inpress",
            detail: "Conference · In Press"
        },
        {
            title: "Comparative Analysis of AI Chip Innovation Strategies: DBSCAN Clustering and SSA Forecasting Applied to Diverse M&A Patterns",
            authors: "B. Zylfiu, G. Marinova",
            venue: "ICISE 2026, Tirana, Albania",
            status: "inpress",
            detail: "Conference · In Press"
        },
    ];

    return (
        <div className="db-page">

            {/* Hero */}
            <div className="db-hero">
                <div>
                    <h1 className="db-hero-title">M&amp;A Research Dashboard</h1>
                    <p className="db-hero-sub">
                        Methods and Online tool for studying the dynamics and evaluating the viability of
                        Electronic Design Automation companies in times of technological disruption
                    </p>
                    <p className="db-hero-meta">
                        <i className="bi bi-person-circle me-1"></i>
                        Blerim Ismajl Zylfiu &nbsp;·&nbsp; Technical University of Sofia
                    </p>
                </div>
                <div className="db-hero-badge">
                    <span className="db-hero-pct">{pct}%</span>
                    <span className="db-hero-pct-label">Research Progress</span>
                </div>
            </div>

            {/* Timeline */}
            <div className="db-card">
                <div className="db-card-header">
                    <i className="bi bi-calendar3"></i>
                    <h5>Research Timeline</h5>
                </div>
                <div className="db-card-body">
                    <div className="db-timeline">
                        <div className="db-tl-step">
                            <div className="db-tl-dot done"><i className="bi bi-check-lg"></i></div>
                            <div className="db-tl-name">Research Phase</div>
                            <div className="db-tl-desc">Literature Review &amp; Analysis</div>
                            <div className="db-tl-date">Completed · 2021–2023</div>
                        </div>
                        <div className="db-tl-step">
                            <div className="db-tl-dot done"><i className="bi bi-check-lg"></i></div>
                            <div className="db-tl-name">Development Phase</div>
                            <div className="db-tl-desc">Platform Development &amp; Testing</div>
                            <div className="db-tl-date">Completed · 2023–2025</div>
                        </div>
                        <div className="db-tl-step">
                            <div className="db-tl-dot active"><i className="bi bi-pencil"></i></div>
                            <div className="db-tl-name">Finalization Phase</div>
                            <div className="db-tl-desc">Dissertation Writing &amp; Defense</div>
                            <div className="db-tl-date">In Progress · 2025</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main grid */}
            <div className="row g-4 align-items-start">
                <div className="col-lg-8">

                    {/* Research Info */}
                    <div className="db-card">
                        <div className="db-card-header">
                            <i className="bi bi-file-earmark-text"></i>
                            <h5>Research Information</h5>
                        </div>
                        <div className="db-card-body" style={{ padding: 0 }}>
                            <div className="db-info-table">
                                <div className="db-info-cell">
                                    <label>Doctoral Student</label>
                                    <p>Blerim Ismajl Zylfiu</p>
                                </div>
                                <div className="db-info-cell">
                                    <label>Supervisor</label>
                                    <p>Assoc. Prof. Dr. Galia Ilieva Marinova</p>
                                </div>
                                <div className="db-info-cell">
                                    <label>Enrollment Date</label>
                                    <p>October 1, 2021</p>
                                </div>
                                <div className="db-info-cell">
                                    <label>Program Deadline</label>
                                    <p>October 1, 2025</p>
                                </div>
                                <div className="db-info-cell">
                                    <label>University</label>
                                    <p>Technical University of Sofia</p>
                                </div>
                                <div className="db-info-cell">
                                    <label>Faculty</label>
                                    <p>Telecommunications</p>
                                </div>
                                <div className="db-info-cell">
                                    <label>Department</label>
                                    <p>Technologies and Management in Communication Systems</p>
                                </div>
                                <div className="db-info-cell">
                                    <label>Study Mode</label>
                                    <p>Part-time Doctoral Studies</p>
                                </div>
                                <div className="db-info-cell full">
                                    <label>Dissertation Topic</label>
                                    <p>Methods and Online tool for studying the dynamics and evaluating the viability of Electronic Design Automation companies in times of technological disruption</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Publications */}
                    <div className="db-card">
                        <div className="db-card-header">
                            <i className="bi bi-journal-bookmark"></i>
                            <h5>Research Publications</h5>
                        </div>
                        <div className="db-card-body">
                            {pubs.map((p, i) => (
                                <div className="db-pub" key={i}>
                                    <div className="db-pub-num">{i + 1}</div>
                                    <div>
                                        <p className="db-pub-title">{p.title}</p>
                                        <p className="db-pub-authors">{p.authors}</p>
                                        {p.venue && <p className="db-pub-venue">{p.venue}</p>}
                                        <span className={`db-pub-status ${p.status}`}>
                                            {p.status === 'published'
                                                ? <><i className="bi bi-check-circle-fill"></i> {p.detail}</>
                                                : <><i className="bi bi-clock-fill"></i> {p.detail}</>}
                                        </span>
                                    </div>
                                </div>
                            ))}
                            <div className="db-pub-footer">
                                <div className="db-pub-stat">
                                    <strong>8</strong>
                                    <span>Total Publications</span>
                                </div>
                                <div className="db-pub-stat">
                                    <strong>6</strong>
                                    <span>Published</span>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="col-lg-4">

                    {/* Progress ring */}
                    <div className="db-card">
                        <div className="db-card-header">
                            <i className="bi bi-graph-up-arrow"></i>
                            <h5>Research Progress</h5>
                        </div>
                        <div className="db-card-body">
                            <div className="db-ring-wrap">
                                <div className="db-ring">
                                    <svg width="130" height="130" viewBox="0 0 130 130">
                                        <circle cx={cx} cy={cx} r={r} fill="none" stroke="#eef0f8" strokeWidth="11" />
                                        <circle
                                            cx={cx} cy={cx} r={r}
                                            fill="none"
                                            stroke="#3b5bdb"
                                            strokeWidth="11"
                                            strokeLinecap="round"
                                            strokeDasharray={circ}
                                            strokeDashoffset={offset}
                                            style={{ transition: 'stroke-dashoffset 0.7s ease' }}
                                        />
                                    </svg>
                                    <div className="db-ring-label">
                                        <span className="db-ring-pct">{pct}%</span>
                                        <span className="db-ring-sub">Complete</span>
                                    </div>
                                </div>
                                <div className="db-time-box">
                                    {moLeft > 0 ? (
                                        <>
                                            <div className="db-time-main">{moLeft} <small>months</small></div>
                                            <div className="db-time-sub">{daysLeft} days remaining</div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="db-time-main" style={{ color: '#16a34a' }}>Done</div>
                                            <div className="db-time-sub">Deadline passed</div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Research stats */}
                    <div className="db-card">
                        <div className="db-card-header">
                            <i className="bi bi-bar-chart-line"></i>
                            <h5>Research Summary</h5>
                        </div>
                        <div className="db-card-body">
                            <div className="db-stat-row">
                                <span className="db-stat-label">M&amp;A Transactions</span>
                                <span className="db-stat-value">661</span>
                            </div>
                            <div className="db-stat-row">
                                <span className="db-stat-label">Date Range</span>
                                <span className="db-stat-value">1975 – 2025</span>
                            </div>
                            <div className="db-stat-row">
                                <span className="db-stat-label">Clusters Identified</span>
                                <span className="db-stat-value">5</span>
                            </div>
                            <div className="db-stat-row">
                                <span className="db-stat-label">Silhouette Score</span>
                                <span className="db-stat-value">0.879</span>
                            </div>
                            <div className="db-stat-row">
                                <span className="db-stat-label">M&amp;A Waves Detected</span>
                                <span className="db-stat-value">30</span>
                            </div>
                            <div className="db-stat-row">
                                <span className="db-stat-label">Publications</span>
                                <span className="db-stat-value">8 (6 published)</span>
                            </div>
                            <div className="db-stat-row">
                                <span className="db-stat-label">Total Citations</span>
                                <span className="db-stat-value">3</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default Dashboard;
